import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { useParams } from 'react-router-dom'
import { COMPLEX, TIMER } from 'utils/constants'
import { addLogicNodeToOutput } from 'actions/feature/logicNodes'
import {
  addLogicNodeToInput,
  addExistingLogicNodeToQuery,
} from 'actions/feature/inputs'
import {
  makeGetNewLogicNodeIndex,
  getLogicNodes,
  getInputRestrictionFromOutput,
} from 'selectors/feature/logicNodes'
import { updateOutputQuery } from 'actions/feature/outputs'
import { Menu } from 'antd'
import {
  MenuItem,
  SubMenu,
} from 'components/patterns/DropdownMenu/DropdownMenu.style'
import strings from 'utils/strings'
import {
  StyledCricleDiv,
  StyledDropdown,
} from 'components/feature/NodeConnector/NodeConnector.style'
import { LIMITED, RESTRICTED } from 'selectors/feature/utils'

export default function QuickAddLogicMenu({ output, condition }) {
  const dispatch = useDispatch()
  const { featureId } = useParams()
  const functionIds = useSelector((state) =>
    state
      .getIn(['feature', 'currentFeature', 'feature', featureId, 'functions'])
      .toJS()
  )
  const restrictions = useSelector(getInputRestrictionFromOutput, {
    id: output?.id,
  })
  const canAddLogicSet = restrictions !== LIMITED
  const canAddTimer = restrictions !== RESTRICTED

  const possibleNodes = useSelector(getLogicNodes) || {}
  const existingNodes =
    functionIds === undefined || functionIds.length === 0
      ? []
      : Object.values(possibleNodes)
          .sort((a, b) => a.description.localeCompare(b.description))
          .filter((node) => {
            if (!node.functionUris) return true
            if (node.queryType === TIMER && !canAddTimer) return false
            if (node.queryType === COMPLEX && !canAddLogicSet) return false
            return true
          })

  const getNewLogicSetIndex = makeGetNewLogicNodeIndex(COMPLEX)
  const getNewTimerIndex = makeGetNewLogicNodeIndex(TIMER)

  const nextLogicSetIndex = useSelector(getNewLogicSetIndex)
  const nextTimerIndex = useSelector(getNewTimerIndex)

  const addNode = ({ key }) => {
    const nextIndex = {
      [TIMER]: nextTimerIndex,
      [COMPLEX]: nextLogicSetIndex,
    }
    if (output) {
      dispatch(addLogicNodeToOutput(key, nextIndex[key], output.fnId))
    }
    if (condition) {
      dispatch(addLogicNodeToInput(key, nextIndex[key], condition.uri))
    }
  }

  const addExistingNode = (node) => () => {
    if (output) {
      dispatch(updateOutputQuery(node.uri, output.fnId))
    }
    if (condition) {
      // update logic set
      dispatch(addExistingLogicNodeToQuery(node, condition))
    }
  }

  const addMenuOptions = (
    <Menu>
      {canAddLogicSet && (
        <MenuItem key="complex" onClick={addNode}>
          {strings.add_new_logic_set}
        </MenuItem>
      )}
      {canAddTimer && (
        <MenuItem key="timer" onClick={addNode}>
          {strings.add_new_timer}
        </MenuItem>
      )}
      {!condition && (
        <MenuItem key="simple" onClick={addNode}>
          {strings.add_new_single_condition}
        </MenuItem>
      )}
      {existingNodes.length > 0 && (
        <StyledSubmenu key="more">
          {existingNodes.map((node, i) => (
            <StyledMenuItem key={node.id} onClick={addExistingNode(node)}>
              {node.description}
            </StyledMenuItem>
          ))}
        </StyledSubmenu>
      )}
    </Menu>
  )

  const left = output ? 15 : 10

  return (
    <StyledMenuWrapper onClick={(e) => e.stopPropagation()} left={left}>
      <StyledDropdown
        size="small"
        clickHandler={addNode}
        trigger="click"
        overlay={addMenuOptions}
        placement="bottomRight"
      >
        <StyledCricleDiv>+</StyledCricleDiv>
      </StyledDropdown>
      <svg
        width={left}
        height={22}
        viewBox={`0 0 ${30} ${30}`}
        data-testid="SVGElm"
        style={{
          position: 'absolute',
          top: 0,
          left: '22px',
        }}
      >
        <g fillRule="nonzero">
          <line
            x1="0"
            y1="50%"
            x2="100%"
            y2="50%"
            stroke="gray"
            strokeWidth="3"
          />
        </g>
      </svg>
    </StyledMenuWrapper>
  )
}

QuickAddLogicMenu.propTypes = {
  output: PropTypes.object,
  condition: PropTypes.object,
}

export const StyledMenuWrapper = styled.div(
  ({ left = 0 }) => `
    position: absolute;
    top: 50%;
    right: calc(100% + ${left}px);
    transform: translateY(-50%);
  `
)

const StyledMenuItem = styled(MenuItem)`
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledSubmenu = styled(SubMenu)`
  .ant-dropdown-menu-submenu-title::before {
    content: 'Select Existing';
  }
`
